/**
 * Team Chat Service
 * 
 * Connects to the chat worker for team messaging.
 * Supports both ephemeral (24h) and immortal (permanent) messages.
 */

const CHAT_WORKER_URL = import.meta.env.VITE_CHAT_URL || 'https://nt3d-chat.wurkit.workers.dev';

export interface ChatMessage {
  id: string;
  teamId: number;
  sender: string;
  senderName?: string;
  content: string;
  timestamp: number;
  type: 'ephemeral' | 'immortal';
  signature?: string;
  txHash?: string;
}

export interface SendMessageRequest {
  teamId: number;
  content: string;
  sender: string;
  senderName?: string;
}

export interface ImmortalizeRequest extends SendMessageRequest {
  signature: string;
  message: string;
}

// Message polling state
let pollingInterval: ReturnType<typeof setInterval> | null = null;
let lastMessageTimestamp = 0;

/**
 * Send an ephemeral team message (lasts 24 hours)
 */
export async function sendMessage(request: SendMessageRequest): Promise<{ success: boolean; messageId?: string; error?: string }> {
  try {
    const response = await fetch(`${CHAT_WORKER_URL}/send`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const error = await response.json();
      return { success: false, error: error.error || 'Failed to send message' };
    }

    return await response.json();
  } catch (error) {
    console.error('Failed to send message:', error);
    return { success: false, error: String(error) };
  }
}

/**
 * Get messages for a team
 */
export async function getMessages(
  teamId: number, 
  since: number = 0, 
  limit: number = 50
): Promise<ChatMessage[]> {
  try {
    const params = new URLSearchParams({
      teamId: String(teamId),
      since: String(since),
      limit: String(limit),
    });

    const response = await fetch(`${CHAT_WORKER_URL}/messages?${params}`);
    if (!response.ok) return [];

    const data = await response.json();
    return data.messages || [];
  } catch (error) {
    console.error('Failed to get messages:', error);
    return [];
  }
}

/**
 * Create an immortal (permanent) message
 * Requires wallet signature for verification
 */
export async function immortalizeMessage(request: ImmortalizeRequest): Promise<{ success: boolean; messageId?: string; error?: string }> {
  try {
    const response = await fetch(`${CHAT_WORKER_URL}/immortalize`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const error = await response.json();
      return { success: false, error: error.error || 'Failed to immortalize message' };
    }

    return await response.json();
  } catch (error) {
    console.error('Failed to immortalize message:', error);
    return { success: false, error: String(error) };
  }
}

/**
 * Get immortal messages
 */
export async function getImmortalMessages(
  roundId?: number, 
  teamId?: number
): Promise<ChatMessage[]> {
  try {
    const params = new URLSearchParams();
    if (roundId) params.set('roundId', String(roundId));
    if (teamId) params.set('teamId', String(teamId));

    const response = await fetch(`${CHAT_WORKER_URL}/immortal?${params}`);
    if (!response.ok) return [];

    const data = await response.json();
    return data.messages || [];
  } catch (error) {
    console.error('Failed to get immortal messages:', error);
    return [];
  }
}

/**
 * Get chat statistics
 */
export async function getChatStats(): Promise<Record<string, number>> {
  try {
    const response = await fetch(`${CHAT_WORKER_URL}/stats`);
    if (!response.ok) return {};
    return await response.json();
  } catch {
    return {};
  }
}

/**
 * Start polling for new messages
 */
export function startMessagePolling(
  teamId: number,
  onNewMessages: (messages: ChatMessage[]) => void,
  intervalMs: number = 5000
): void {
  // Stop any existing polling
  stopMessagePolling();

  // Initial fetch
  getMessages(teamId, lastMessageTimestamp).then(messages => {
    if (messages.length > 0) {
      lastMessageTimestamp = messages[messages.length - 1].timestamp;
      onNewMessages(messages);
    }
  });

  // Start polling
  pollingInterval = setInterval(async () => {
    const messages = await getMessages(teamId, lastMessageTimestamp);
    if (messages.length > 0) {
      lastMessageTimestamp = messages[messages.length - 1].timestamp;
      onNewMessages(messages);
    }
  }, intervalMs);
}

/**
 * Stop polling for messages
 */
export function stopMessagePolling(): void {
  if (pollingInterval) {
    clearInterval(pollingInterval);
    pollingInterval = null;
  }
  lastMessageTimestamp = 0;
}

/**
 * Create the message to sign for immortalization
 */
export function createImmortalSignMessage(
  content: string,
  teamId: number
): string {
  const timestamp = Date.now();
  return `No Tomorrow 3D Immortal Message

Team: ${teamId}
Content: ${content}
Timestamp: ${timestamp}

By signing this message, I confirm this message will be permanently stored.`;
}

/**
 * Check if chat service is healthy
 */
export async function checkChatHealth(): Promise<boolean> {
  try {
    const response = await fetch(`${CHAT_WORKER_URL}/health`);
    const data = await response.json();
    return data.status === 'ok';
  } catch {
    return false;
  }
}
